import {
  Car,
  Zap,
  Landmark,
  HeartPulse,
  Scale,
  Factory,
  Briefcase,
  ShoppingCart,
  Cloud,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface SectorCard {
  /** URL slug used to look up the card from a detail page. */
  id: string;
  /** Link target for the card / route path. */
  path: string;
  icon: LucideIcon;
  title: string;
  description: string;
  /** Background image, shared by the card and the sector page hero. */
  image: string;
  imageAlt: string;
}

export const sectorCards: SectorCard[] = [
  {
    id: 'automotive',
    path: '/sectors/automotive',
    icon: Car,
    title: 'Automotive',
    description:
      'Vehicle manufacturers navigating UN R155/R156, connected car security, and software-defined vehicles.',
    image: '/images/sectors/automotive.avif',
    imageAlt: 'Connected vehicles on a modern production line',
  },
  {
    id: 'energy',
    path: '/sectors/energy',
    icon: Zap,
    title: 'Energy & Utilities',
    description:
      'Critical infrastructure operators securing OT/ICS environments and achieving NIS2 compliance.',
    image: '/images/sectors/energy.avif',
    imageAlt: 'Electricity transmission infrastructure at dusk',
  },
  {
    id: 'finance',
    path: '/sectors/finance',
    icon: Landmark,
    title: 'Financial Services',
    description:
      'Banks, fintechs, insurers, and investment firms facing FCA regulations and sophisticated cyber threats.',
    image: '/images/sectors/finance.avif',
    imageAlt: 'Financial district skyline',
  },
  {
    id: 'healthcare',
    path: '/sectors/healthcare',
    icon: HeartPulse,
    title: 'Healthcare',
    description:
      'NHS trusts, clinics, and healthtech companies protecting patient data and medical infrastructure.',
    image: '/images/sectors/healthcare.avif',
    imageAlt: 'Clinician using technology in a healthcare setting',
  },
  {
    id: 'legal',
    path: '/sectors/legal',
    icon: Scale,
    title: 'Legal',
    description:
      'Law firms safeguarding client confidentiality and privileged information from cyber threats.',
    image: '/images/sectors/legal.avif',
    imageAlt: 'Legal library and scales of justice',
  },
  {
    id: 'manufacturing',
    path: '/sectors/manufacturing',
    icon: Factory,
    title: 'Manufacturing',
    description:
      'Industrial and manufacturing companies protecting IP, OT systems, and supply chains.',
    image: '/images/sectors/manufacturing.avif',
    imageAlt: 'Automated industrial manufacturing line',
  },
  {
    id: 'private-equity',
    path: '/sectors/private-equity',
    icon: Briefcase,
    title: 'Private Equity',
    description: 'PE firms and portfolio companies managing cyber risk across investments.',
    image: '/images/sectors/private-equity.avif',
    imageAlt: 'Investment professionals reviewing a deal',
  },
  {
    id: 'retail',
    path: '/sectors/retail',
    icon: ShoppingCart,
    title: 'Retail',
    description: 'Retailers and e-commerce businesses securing customer data and payment systems.',
    image: '/images/sectors/retail.avif',
    imageAlt: 'Customer making a contactless payment in a retail store',
  },
  {
    id: 'saas',
    path: '/sectors/saas',
    icon: Cloud,
    title: 'SaaS & Technology',
    description:
      'Software companies achieving SOC 2, ISO 27001, and meeting enterprise customer security requirements.',
    image: '/images/sectors/saas.avif',
    imageAlt: 'Cloud data centre server infrastructure',
  },
];

export const getSectorCard = (id: string): SectorCard | undefined =>
  sectorCards.find(sector => sector.id === id);
